import { DialogBody, DialogLayout, InlineError } from '@/ui';
import { dialogRhythm } from '@/ui/composites/dialogRhythm';
import { Button, type DialogProps } from '@/ui/primitives';
import type { KeyboardEvent, ReactNode } from 'react';

export type AddDialogProps = {
  open: boolean;
  title: string;
  saving?: boolean;
  disableSave?: boolean;
  saveLabel?: string;
  description?: ReactNode;
  errorMessage?: string | null;
  hideCancel?: boolean;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  width?: 'standard' | 'wide';
  onClose: () => void;
  onDialogClose?: DialogProps['onClose'];
  onSave: () => void | Promise<void>;
  children: ReactNode;
};

export const AddDialog = ({
  open,
  title,
  saving = false,
  disableSave = false,
  saveLabel = 'Save',
  description,
  errorMessage,
  hideCancel = false,
  disableClose = false,
  allowTopBarInteraction = false,
  width = 'standard',
  onClose,
  onDialogClose,
  onSave,
  children,
}: AddDialogProps): ReactNode => {
  const handleContentKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (saving || disableSave) return;
    if (event.defaultPrevented || event.key !== 'Enter') return;
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName;
    if (tagName === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    void onSave();
  };
  return (
    <DialogLayout
      open={open}
      onClose={onDialogClose ?? onClose}
      disableClose={disableClose}
      allowTopBarInteraction={allowTopBarInteraction}
      width={width}
      title={title}
      description={description}
      onContentKeyDownCapture={handleContentKeyDownCapture}
      actions={
        <>
          {hideCancel ? null : (
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={saving}
              aria-label="Cancel dialog"
            >
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            loading={saving}
            onClick={() => {
              void onSave();
            }}
            disabled={saving || disableSave}
            aria-label="Save dialog"
          >
            {saveLabel}
          </Button>
        </>
      }
    >
      <DialogBody gap={dialogRhythm.bodyGap}>
        {children}
        {errorMessage ? <InlineError message={errorMessage} /> : null}
      </DialogBody>
    </DialogLayout>
  );
};
